// Profile-aware "entry fit" — melds the longitudinal district trend with the
// snapshot timing signal and affordability into ONE score + a decision label,
// weighted by the user's own decision-making posture inferred from their
// profile. Own-stay focus: affordability always carries weight; the longitudinal
// trajectory and snapshot timing modulate *when* to move.
//
// This is an ADDITIVE layer. It does not replace the affordability-band and
// entry-score logic in regions.ts / affordability.ts — it consumes their output
// (plus the /api/private-trends longitudinal read) and reweights it per profile.
// Pure functions, browser-only, no I/O. Decision support, not advice.

import type { Profile } from "../profile/schema";
import type { DistrictTrend } from "../api";

// Where the buyer sits on a patient value-hunter ↔ worried-about-being-priced-out
// axis, in [0,1]. 0 = all the time in the world, optimise for a below-trend dip;
// 1 = time-pressured, optimise for getting in while it still fits.
export interface EntryStance {
  urgency: number; // 0..1
  reasons: string[]; // human-readable drivers, for the rationale line
}

const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));
const clamp100 = (x: number) => Math.max(0, Math.min(100, x));

/**
 * Infer the buyer's entry stance from existing profile fields — no new inputs.
 * Own-stay lens: life-stage pressure (age, school-age children, an upgrade the
 * household has outgrown) pushes toward urgency; a long horizon and affordability
 * headroom pull back toward patience.
 */
export function inferEntryStance(p: Profile): EntryStance {
  let urgency = 0.5;
  const reasons: string[] = [];

  // Horizon: older buyers have less runway to wait out a cycle.
  const ageAdj = clamp((p.ageYears - 35) / 100, -0.2, 0.2);
  urgency += ageAdj;
  if (p.ageYears >= 50) reasons.push("shorter horizon to hold a mortgage");
  else if (p.ageYears <= 30) reasons.push("long horizon — room to wait for a dip");

  // School-age children: 1km/2km priority zones make timing location-critical.
  const stage = p.household?.childrenSchoolingStage;
  if (stage === "primary" || stage === "secondary") {
    urgency += 0.15;
    reasons.push("school-age children — a location window you can't easily defer");
  } else if (stage === "preschool") {
    urgency += 0.05;
    reasons.push("a school-zone move on the horizon");
  }

  // Already own and upgrading: a move the household has decided on — more urgent
  // than a first-timer who can simply keep renting and watch.
  const owns = (p.currentPropertyValue ?? 0) > 0;
  if (owns && p.buyIntent !== "second") {
    urgency += 0.1;
    reasons.push("an upgrade you're actively planning");
  }
  if (p.household && p.household.children > 0 && !owns) {
    urgency += 0.05;
    reasons.push("a growing household");
  }

  return { urgency: clamp(urgency), reasons };
}

// Affordability ratio -> 0..100 (mirrors feasibility(): 0.7x=0, 1.0x=60, 1.2x=100).
export function affordabilityScore(ratio: number): number {
  return clamp100((ratio - 0.7) * 200);
}

/**
 * Longitudinal "value timing" score, 0..100 (higher = cheaper vs its own
 * multi-year trajectory, i.e. a better value window). Combines where price sits
 * vs its own long trend (trendZ) with the recent direction (slope). Below-trend
 * and cooling score high; stretched-and-rising scores low. Returns null when the
 * district has no readable trend yet.
 */
export function trendValueScore(t: Pick<DistrictTrend, "trendZ" | "slopePctPerMonth" | "direction"> | null): number | null {
  if (!t) return null;
  const z = t.trendZ ?? null;
  const slope = t.slopePctPerMonth ?? null;
  if (z === null && slope === null) return null;
  // z of -2 (well below trend) => ~100; 0 => 50; +2 => ~0.
  const zScore = z === null ? 50 : clamp100(50 - z * 25);
  // slope in %/month: -0.5%/m => ~80; 0 => 50; +0.5%/m => ~20 (chasing a rally).
  const slopeScore = slope === null ? 50 : clamp100(50 - slope * 100 * 6);
  return Math.round(0.6 * zScore + 0.4 * slopeScore);
}

export type EntryDecision = "enter-now" | "watch-dip" | "smaller-unit" | "beyond";

export interface EntryFitInput {
  band: "within" | "reachable" | "stretch" | "beyond";
  ratio: number;
  entryScore: number | null; // snapshot timing signal (0..100)
  trend: Pick<DistrictTrend, "trendZ" | "slopePctPerMonth" | "direction"> | null;
  stance: EntryStance;
}

export interface EntryFit {
  score: number; // 0..100 blended entry fit
  decision: EntryDecision;
  label: string;
  rationale: string;
  weights: { afford: number; timing: number; trend: number };
  components: { afford: number; timing: number; trend: number | null };
}

const DECISION_LABEL: Record<EntryDecision, string> = {
  "enter-now": "Enter now",
  "watch-dip": "Watch for a dip",
  "smaller-unit": "Stretch — smaller unit",
  beyond: "Beyond reach",
};

/**
 * Blend affordability + snapshot timing + longitudinal trend into one entry-fit
 * score and a decision, weighted by the inferred stance. Urgency shifts weight
 * toward affordability (get in while it fits) and away from waiting-for-a-dip
 * value timing; patience does the reverse.
 */
export function scoreEntryFit(inp: EntryFitInput): EntryFit {
  const u = inp.stance.urgency;
  const affScore = affordabilityScore(inp.ratio);
  const timingScore = inp.entryScore ?? 50;
  const trendScore = trendValueScore(inp.trend);

  // Weights (own-stay). Affordability dominates and grows with urgency; the
  // longitudinal value-timing weight shrinks with urgency. Renormalised so they
  // always sum to 1 even when the trend component is missing.
  let wAfford = 0.45 + 0.15 * u;
  let wTiming = 0.3 - 0.05 * u;
  let wTrend = trendScore === null ? 0 : 0.25 - 0.1 * u;
  const wSum = wAfford + wTiming + wTrend;
  wAfford /= wSum; wTiming /= wSum; wTrend /= wSum;

  const score = Math.round(
    wAfford * affScore + wTiming * timingScore + wTrend * (trendScore ?? 0),
  );

  const { decision, rationale } = decide(inp, { affScore, timingScore, trendScore, urgency: u });
  return {
    score,
    decision,
    label: DECISION_LABEL[decision],
    rationale,
    weights: { afford: round2(wAfford), timing: round2(wTiming), trend: round2(wTrend) },
    components: { afford: Math.round(affScore), timing: Math.round(timingScore), trend: trendScore },
  };
}

function decide(
  inp: EntryFitInput,
  s: { affScore: number; timingScore: number; trendScore: number | null; urgency: number },
): { decision: EntryDecision; rationale: string } {
  const cheapVsHistory = s.trendScore !== null && s.trendScore >= 62;
  const stretchedVsHistory = s.trendScore !== null && s.trendScore <= 40;
  const softSnapshot = inp.entryScore !== null && inp.entryScore >= 62;
  const stretchedSnapshot = inp.entryScore !== null && inp.entryScore <= 40;
  const urgent = s.urgency >= 0.6;
  const patient = s.urgency <= 0.4;
  const stanceWhy = inp.stance.reasons[0] ? ` (${inp.stance.reasons[0]})` : "";

  if (inp.band === "beyond") {
    return { decision: "beyond", rationale: "Beyond reach at your target size — a smaller unit or more buying power first." };
  }
  if (inp.band === "stretch") {
    return softSnapshot || cheapVsHistory
      ? { decision: "smaller-unit", rationale: "A stretch, but priced softly vs trend — feasible with a smaller unit or a bigger downpayment." }
      : { decision: "smaller-unit", rationale: "A stretch and not especially soft — a smaller unit or another district reads better." };
  }

  // within / reachable
  if (cheapVsHistory || (softSnapshot && !stretchedVsHistory)) {
    return { decision: "enter-now", rationale: `Within budget, and prices sit below their own multi-year trend${patient ? "; this market has historically rewarded patient buyers" : ""}. Timing read is experimental.` };
  }
  if (urgent) {
    // Time-pressured: if it fits, waiting for a dip carries its own cost.
    return stretchedSnapshot || stretchedVsHistory
      ? { decision: "enter-now", rationale: `Within budget; prices are firm${stanceWhy}. The timing read is experimental — the right unit matters more than the month.` }
      : { decision: "enter-now", rationale: `Within budget; timing is neutral${stanceWhy}. The right unit matters more than the month.` };
  }
  if (stretchedSnapshot || stretchedVsHistory) {
    return { decision: "watch-dip", rationale: "Within budget; prices look stretched against their own trend (experimental read)." };
  }
  return { decision: "watch-dip", rationale: "Within budget; timing is neutral (experimental read)." };
}

const round2 = (x: number) => Math.round(x * 100) / 100;
